/**
 * Object owning app configuration.
 * Works as a provider of classes like
 * services/managers that are app-wide
 * and used by all modules (eg.: NavigationManagerWeb).
 */
import {Platforms} from "./constants";
import {Events, IAppContext} from "./interfaces/i-app-context";
import {IStorage} from "./interfaces/i-storage";
import {EventTarget} from "@kta20-core/core-module";
import {Storage} from "@kta20-core/data/storage";


const NOT_AVAILABLE_MSG = 'On @event listener not available on this platform';

const LINK_NAME = 'kta20dynamicLink';

class AppContextWeb extends EventTarget implements IAppContext {

    _platform: Platforms;
    _deploymentProfile: string;
    _ctxAPI: any;
    _objectStorage:IStorage;
    _uiEngine:any;
    _isReady:boolean=false;

    get platform(): Platforms {
        return this._platform;
    }

    get deploymentProfile(): string {
        return this._deploymentProfile;
    }

    get ctxAPI(): Window {
        return this._ctxAPI;
    }

    get objectStorage():IStorage{
        return this._objectStorage;
    }

    get uiEngine():any{
        return this._uiEngine;
    }

    static get(platform:Platforms,onContextConfigured:(appContext:IAppContext)=>void) {
        const inst = new AppContextWeb();

        //NOTE: here is safe to refer to browser-based API
        // since the NATIVE platform for now works
        //with a webview and the app runs into it.
        inst._uiEngine = document;
        inst._ctxAPI = window;
        inst.ctxAPI.name = 'Window';
        inst._platform = platform;
        inst._objectStorage = new Storage();

        inst.ctxAPI.addEventListener('load', (event:Event)=>{
            inst._isReady = true;
            event.stopImmediatePropagation();
            inst.dispatchEvent(new Event(Events.ON_RESUME));
            inst.dispatchEvent(new Event(Events.ON_DEVICE_READY));
        });

        onContextConfigured(inst);
    }

    configure(): void {
        try{
            navigator.serviceWorker
                .register('/sw.js')
                .then(()=>{ console.log("Service Worker Registered"); });
        }
        catch (e) {
            console.warn(e);
        }
    }

    isReady(): boolean {
        return this._isReady;
    }

    async renderMain(name:string,opts:any):Promise<void> {
        const mainElem = document.createElement(name);
        for (let p in opts)
            mainElem[p]=opts[p];

        document.body.appendChild(mainElem);
    }


    async openFile(filePath:string, mimeType:string, options:any=null){
        window.open(filePath, options); mimeType;
    }

    async downloadFileAsBlob(nameToUser:string, blob:Blob, mimeType:string):Promise<string>{
        nameToUser = `${nameToUser}.${mimeType.split('/')[1]}`; //get extension

        let a = document.getElementById(LINK_NAME) || document.createElement("a");
        a.id = LINK_NAME;
        a['href'] = URL.createObjectURL(blob);
        a.setAttribute("download", nameToUser);
        a.click();

        //returning null since the client will not
        // handle opening that is done here already
        return null;
    }

    addOnPauseListener(handler) {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_PAUSE));
    }

    removeOnPauseListener(handler) {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_PAUSE));
    }

    /**
     * ON the web app (browser context)
     * this event is fired only once
     * when the document is loaded.
     * @param handler
     */
    addOnResumeListener(handler) {
        this.addEventListener(Events.ON_RESUME, handler);
    }

    removeOnResumeListener(handler) {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_RESUME));
    }

    addOnDeviceReadyListener(handler) {
        this.addEventListener(Events.ON_DEVICE_READY, handler);
    }

    removeOnDeviceReadyListener(handler) {
        this.removeEventListener(Events.ON_DEVICE_READY, handler);
    }

    addOnSearchListener(handler) {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_SEARCH));
    }

    removeOnSearchListener(handler) {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_SEARCH));
    }

    addOnMenuListener(handler) {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_MENU));
    }

    removeOnMenuListener(handler) {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_MENU));
    }

    //TODO: not really true! You can use "state change" event if you push/pop states into history
    addOnBackListener(handler) {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_BACK));
    }

    removeOnBackListener(handler) {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_BACK));
    }

    addOnKeyboardDidHideListener(handler): void {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_KEYBOARD_DID_HIDE));
    }

    removeOnKeyboardDidHideListener(handler): void {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_KEYBOARD_DID_HIDE));
    }

    addOnKeyboardDidShowListener(handler): void {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_KEYBOARD_DID_SHOW));
    }

    removeOnKeyboardDidShowListener(handler): void {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_KEYBOARD_DID_SHOW));
    }

    addOnKeyboardWillHideListener(handler): void {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_KEYBOARD_WILL_HIDE));
    }

    removeOnKeyboardWillHideListener(handler): void {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_KEYBOARD_WILL_HIDE));
    }

    addOnKeyboardWillShowListener(handler): void {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_KEYBOARD_WILL_SHOW));
    }

    removeOnKeyboardWillShowListener(handler): void {
        handler;
        console.warn(NOT_AVAILABLE_MSG.replace('@event',Events.ON_KEYBOARD_WILL_SHOW));
    }

}

export {AppContextWeb as AppContext};
